import React, { useCallback, useMemo } from 'react';
import { Box, Switch, Typography } from '@mui/material';
import {
  ElementSelectPropertyCountType,
  IElementSelectProperty,
  IElementSwitchProperty,
  type IElementProperty,
} from '@shared/models/element-property.model';
import type { PropertyChange } from '@shared/helpers/element-property.helpers';
import {
  getCount,
  getSelectPropertyOptions,
  isElementSelectProperty,
  isElementSwitchProperty,
} from '@shared/helpers/element-property.helpers';
import { PropertyMenu } from '@ui/menus/PropertyMenu';

type CategoryPickerPropertyFieldProps = {
  property: IElementProperty;
  onPropertyChange: (change: PropertyChange) => void;
};

/**
 * Local category property control for the EPD dialog (no project hooks).
 */
export const CategoryPickerPropertyField: React.FC<
  CategoryPickerPropertyFieldProps
> = ({ property, onPropertyChange }) => {
  if (isElementSelectProperty(property)) {
    return (
      <SelectField property={property} onPropertyChange={onPropertyChange} />
    );
  }

  if (isElementSwitchProperty(property)) {
    return (
      <SwitchField property={property} onPropertyChange={onPropertyChange} />
    );
  }

  return null;
};

const SelectField: React.FC<{
  property: IElementSelectProperty;
  onPropertyChange: CategoryPickerPropertyFieldProps['onPropertyChange'];
}> = ({ property, onPropertyChange }) => {
  const options = useMemo(() => getSelectPropertyOptions(property), [property]);
  const count = getCount(property, false);
  const selectedOption = options.find((option) => option.value === count);

  const handleSelect = useCallback(
    (value: ElementSelectPropertyCountType) => {
      onPropertyChange({ ...property, count: value });
    },
    [onPropertyChange, property],
  );

  return (
    <PropertyRow property={property}>
      <PropertyMenu
        options={options}
        selectedValue={count}
        selectedLabel={selectedOption?.label ?? count?.toString() ?? 'None'}
        isFallback={!property.count}
        buttonLabelProps={{
          sx: {
            textTransform: 'capitalize',
          },
        }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        onSelect={handleSelect}
      />
    </PropertyRow>
  );
};

const SwitchField: React.FC<{
  property: IElementSwitchProperty;
  onPropertyChange: CategoryPickerPropertyFieldProps['onPropertyChange'];
}> = ({ property, onPropertyChange }) => {
  const count = getCount(property);

  const handleChange = useCallback(
    (_event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
      if (checked !== count) onPropertyChange({ ...property, count: checked });
    },
    [count, onPropertyChange, property],
  );

  return (
    <PropertyRow property={property}>
      <Switch size="small" checked={!!count} onChange={handleChange} />
    </PropertyRow>
  );
};

const PropertyRow: React.FC<{
  property: IElementProperty;
  children: React.ReactNode;
}> = ({ property, children }) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: 2,
    }}
  >
    <Typography
      variant="caption"
      title={property.description ?? property.displayName ?? property.name}
    >
      {property.displayName ?? property.name}
    </Typography>
    {children}
  </Box>
);
